import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randAlphaNumeric,
  randChanceBoolean,
  randNumber,
  randProductName,
} from '@ngneat/falso'

import type { MirageServer, MockServer } from './server.js'
import { createErrorResponse, filterable, pageable } from './utils.js'

type ProviderMedicineAttributes = {
  name: string
  brand_name: string
  vnr_number: string
  strength_text: string
  package_description: string
  target_group: 'VET' | 'HUMAN' | 'BOTH'
  controlled_substance: boolean
  active_substance: string
  display_price: number
}

export const model = Model as ModelDefinition<ProviderMedicineAttributes>

const ACTIVE_SUBSTANCES = [
  'Meloxicam',
  'Amoxicillin',
  'Dexmedetomidine',
  'Ibuprofen',
  'Metronidazole',
  'Enrofloxacin',
  'Prednisolone',
  'Furosemide',
  'Atenolol',
  'Tramadol',
]

const DOSAGE_FORMS = [
  'oral suspension',
  'solution for injection',
  'tablets',
  'capsules',
  'eye drops',
  'cream',
]

const PACKAGE_UNITS = ['ml bottle', 'ml vial', 'tablets', 'capsules', 'g tube']

export const factory = Factory.extend<ProviderMedicineAttributes>({
  brand_name() {
    return randProductName()
  },
  strength_text() {
    return `${randNumber({ min: 1, max: 500 })} ${rand(['mg', 'mg/ml', 'mg/5 ml', 'mcg'])}`
  },
  name() {
    return `${this.brand_name as string} ${this.strength_text as string} ${rand(DOSAGE_FORMS)}`
  },
  vnr_number() {
    return randAlphaNumeric({ length: 6 }).join('').toUpperCase()
  },
  package_description() {
    return `${randNumber({ min: 5, max: 200 })} ${rand(PACKAGE_UNITS)}`
  },
  target_group() {
    return rand(['VET', 'HUMAN', 'BOTH'] as const)
  },
  controlled_substance() {
    return randChanceBoolean({ chanceTrue: 0.2 })
  },
  active_substance() {
    return rand(ACTIVE_SUBSTANCES)
  },
  display_price() {
    return randNumber({ min: 2, max: 150, fraction: 2 })
  },
})

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/provider_medicines/search/providers',
    () => ({
      data: [
        {
          type: 'ProviderInfo',
          id: 'pharmaca',
          attributes: {
            name: 'Pharmaca Fennica',
            is_default: true,
          },
        },
        {
          type: 'ProviderInfo',
          id: 'demo_provider',
          attributes: {
            name: 'Demo provider',
            is_default: false,
          },
        },
      ],
      meta: {
        pagination: {
          count: 2,
          page: 1,
          pages: 1,
        },
      },
    }),
  )

  server.get(
    '/consultations/:parent_lookup_consultation/provider_medicines/search',
    function (this: MirageServer, schema, request) {
      const medicines = filterable(schema.all('providerMedicine'), request, {
        provider_code: () => true,
        search: (item, value) => {
          if (!value) {
            return true
          }
          const query = value.toLowerCase()
          return (
            item.attrs.name.toLowerCase().includes(query) ||
            item.attrs.brand_name.toLowerCase().includes(query) ||
            item.attrs.active_substance.toLowerCase().includes(query) ||
            item.attrs.vnr_number.toLowerCase().includes(query)
          )
        },
        target_group: (item, value) =>
          item.attrs.target_group === value ||
          item.attrs.target_group === 'BOTH',
      })

      const { data, meta } = pageable(medicines, request)
      return {
        ...this.serialize(data),
        meta: { pagination: meta },
      }
    },
  )

  server.post(
    '/consultations/:parent_lookup_consultation/provider_medicines/search/:id/convert',
    function (this: MirageServer, schema, request) {
      const medicine = schema.find('providerMedicine', request.params.id)

      if (!medicine) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Medicine not found' }],
        })
      }

      const orgItemId = 9000 + parseInt(medicine.id ?? '0')

      return {
        data: {
          type: 'OrganizationMedicine',
          id: String(orgItemId),
          attributes: {
            id: orgItemId,
            name: medicine.attrs.name,
            package_description: medicine.attrs.package_description,
            unit_description: medicine.attrs.strength_text,
            type_code: 2,
            dosage_form: null,
          },
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.create('providerMedicine', {
    name: 'Testomycin 50 mg/ml oral suspension for dogs and cats',
    brand_name: 'Testomycin',
    vnr_number: 'TEST001',
    strength_text: '50 mg/ml',
    package_description: '100 ml bottle',
    target_group: 'VET',
    controlled_substance: false,
    active_substance: 'Amoxicillin',
    display_price: 12.5,
  })
  server.create('providerMedicine', {
    name: 'Testocillin 250 mg tablets',
    brand_name: 'Testocillin',
    vnr_number: 'TEST002',
    strength_text: '250 mg',
    package_description: '20 tablets',
    target_group: 'BOTH',
    controlled_substance: false,
    active_substance: 'Metronidazole',
    display_price: 8.9,
  })
  server.createList('providerMedicine', 2)
}
